import { createAuthenticatedGraphQLClient } from '../../auth/createAuthenticatedGraphQLClient';
import {
  CanvassingSessionCalendarPreset,
  CanvassingSessionGpsTraceMode,
  CanvassingSessionListSort,
  CanvassingSessionRecordingPurpose,
  CanvassingSessionRecordingStatus,
  CanvassingSessionRecordingWarning,
  SessionTelemetryCategory,
} from '../../graphql/__generated__/graphql';
import type { BulkCanvassingSessionTranscriptionFilter } from '../../graphql/__generated__/graphql';
import { resolveClientEnv } from '../../graphql/config/resolveClientEnv';
import { GraphQLRequestError } from '../../graphql/routing/apiRouting';
import type {
  CanvassingSessionFilterOptionsQuery,
  CanvassingSessionFilterOptionsQueryVariables,
} from '../../graphql/queries/canvassingSessionFilterOptions.generated';
import type {
  CanvassingSessionDetailQuery,
  CanvassingSessionDetailQueryVariables,
} from '../../graphql/queries/canvassingSessionDetail.generated';
import type {
  CanvassingSessionDoorVisitListQuery,
  CanvassingSessionDoorVisitListQueryVariables,
} from '../../graphql/queries/canvassingSessionDoorVisitList.generated';
import type {
  CanvassingSessionListQuery,
  CanvassingSessionListQueryVariables,
} from '../../graphql/queries/canvassingSessionList.generated';
import type {
  CanvassingSessionPipelineJobListQuery,
  CanvassingSessionPipelineJobListQueryVariables,
} from '../../graphql/queries/canvassingSessionPipelineJobList.generated';
import type {
  CanvassingSessionGpsTraceQuery,
  CanvassingSessionGpsTraceQueryVariables,
} from '../../graphql/queries/canvassingSessionGpsTrace.generated';
import type {
  CanvassingSessionTelemetryQuery,
  CanvassingSessionTelemetryQueryVariables,
} from '../../graphql/queries/canvassingSessionTelemetry.generated';
import type {
  CanvassingSessionRecordingQuery,
  CanvassingSessionRecordingQueryVariables,
} from '../../graphql/queries/canvassingSessionRecording.generated';
import {
  CANVASSING_SESSION_DETAIL_DOCUMENT,
  CANVASSING_SESSION_DOOR_VISIT_LIST_DOCUMENT,
  CANVASSING_SESSION_FILTER_OPTIONS_DOCUMENT,
  CANVASSING_SESSION_GPS_TRACE_DOCUMENT,
  CANVASSING_SESSION_LIST_DOCUMENT,
  CANVASSING_SESSION_PIPELINE_JOB_LIST_DOCUMENT,
  CANVASSING_SESSION_RECORDING_DOCUMENT,
  CANVASSING_SESSION_TELEMETRY_DOCUMENT,
} from '../canvassingSessionDocuments';
import {
  CANVASSING_SESSION_CALENDAR_PRESETS,
  isCanvassingSessionCustomDateRangeValid,
  type CanvassingSessionFilterState,
} from './canvassingSessionFilters';

export type CanvassingSessionFetchImpl = NonNullable<Parameters<typeof createAuthenticatedGraphQLClient>[0]['fetchImpl']>;

export type FetchCanvassingSessionsOptions = {
  filters: CanvassingSessionFilterState;
  first: number;
  offset: number;
  fetchImpl?: CanvassingSessionFetchImpl;
};

export type FetchCanvassingSessionFilterOptionsOptions = {
  fetchImpl?: CanvassingSessionFetchImpl;
};

export type CanvassingSessionDetailPagination = {
  first: number;
  offset: number;
};

export type FetchCanvassingSessionDetailCycleOptions = {
  sessionId: string;
  pipelineJobs: CanvassingSessionDetailPagination;
  doorVisits: CanvassingSessionDetailPagination;
  signal: AbortSignal;
  fetchImpl?: CanvassingSessionFetchImpl;
};

export const CANVASSING_SESSION_GPS_TRACE_MAX_PAGE_SIZE = 2_000;

export type FetchCanvassingSessionGpsTraceOptions = {
  sessionId: string;
  mode: CanvassingSessionGpsTraceMode;
  first: number;
  offset: number;
  snapshot?: string;
  signal: AbortSignal;
  fetchImpl?: CanvassingSessionFetchImpl;
};

export const CANVASSING_SESSION_TELEMETRY_PAGE_SIZE_OPTIONS = [12, 25, 50] as const;

export type CanvassingSessionTelemetryPageSize = (typeof CANVASSING_SESSION_TELEMETRY_PAGE_SIZE_OPTIONS)[number];

export type FetchCanvassingSessionTelemetryOptions = {
  sessionId: string;
  first: number;
  offset: number;
  snapshot?: string;
  category?: SessionTelemetryCategory;
  failuresOnly: boolean;
  signal: AbortSignal;
  fetchImpl?: CanvassingSessionFetchImpl;
};

export type FetchCanvassingSessionRecordingOptions = {
  sessionId: string;
  purpose: CanvassingSessionRecordingPurpose;
  signal: AbortSignal;
  fetchImpl?: CanvassingSessionFetchImpl;
};

export type CanvassingSessionRecordingResult = CanvassingSessionRecordingQuery['canvassingSessionRecording'];

export type InterpretedCanvassingSessionRecording =
  | {
      kind: 'ready';
      url: string;
      warnings: readonly CanvassingSessionRecordingWarning[];
    }
  | { kind: 'not-merged' }
  | { kind: 'unavailable' }
  | { kind: 'unexpected' };

export type CanvassingSessionDetailCycle = {
  detail: CanvassingSessionDetailQuery['canvassingSessionDetail'];
  pipelineJobs: NonNullable<CanvassingSessionPipelineJobListQuery['canvassingSessionPipelineJobList']>;
  doorVisits: NonNullable<CanvassingSessionDoorVisitListQuery['canvassingSessionDoorVisitList']>;
};

export type CanvassingSessionRequestError = {
  kind: 'forbidden' | 'request';
  message: string;
};

export type CanvassingSessionTelemetryRequestError = {
  kind: 'forbidden' | 'request';
  message: string;
};

const graphQLErrorHasCode = (error: GraphQLRequestError, expectedCode: string): boolean =>
  (error.graphQLErrors ?? []).some(entry => {
    if (typeof entry !== 'object' || entry === null) return false;
    const extensions = Reflect.get(entry, 'extensions');
    return typeof extensions === 'object' && extensions !== null && Reflect.get(extensions, 'code') === expectedCode;
  });

export const isCanvassingSessionPermissionError = (error: unknown): boolean =>
  error instanceof GraphQLRequestError && (error.status === 403 || graphQLErrorHasCode(error, 'FORBIDDEN'));

export const toCanvassingSessionRequestError = (error: unknown, fallbackMessage: string): CanvassingSessionRequestError => {
  if (isCanvassingSessionPermissionError(error)) {
    return {
      kind: 'forbidden',
      message: 'You do not have permission to view canvassing sessions.',
    };
  }

  return {
    kind: 'request',
    message: error instanceof Error ? error.message : fallbackMessage,
  };
};

export const toCanvassingSessionTelemetryRequestError = (
  error: unknown,
): CanvassingSessionTelemetryRequestError => {
  if (isCanvassingSessionPermissionError(error)) {
    return {
      kind: 'forbidden',
      message: 'You do not have permission to view mobile telemetry.',
    };
  }

  return {
    kind: 'request',
    message: 'Could not load mobile telemetry. Please try again.',
  };
};

export const buildCanvassingSessionFilterVariables = (
  filters: CanvassingSessionFilterState,
): BulkCanvassingSessionTranscriptionFilter => {
  const variables: BulkCanvassingSessionTranscriptionFilter = {
    sort: filters.sort === 'oldest' ? CanvassingSessionListSort.Oldest : CanvassingSessionListSort.Newest,
    calendar:
      filters.dateRange === 'custom'
        ? {
            preset: CanvassingSessionCalendarPreset.Custom,
            ...(filters.fromDate === undefined ? {} : { fromDate: filters.fromDate }),
            ...(filters.throughDate === undefined ? {} : { throughDate: filters.throughDate }),
          }
        : { preset: CANVASSING_SESSION_CALENDAR_PRESETS[filters.dateRange] },
  };

  if (filters.dateRange === 'custom' && !isCanvassingSessionCustomDateRangeValid(filters.fromDate, filters.throughDate)) {
    throw new Error('A valid custom canvassing session date range is required.');
  }
  if (filters.canvasserId) variables.canvasserId = filters.canvasserId;
  if (filters.captureStatus) variables.captureStatus = filters.captureStatus;
  if (filters.processingStatus) variables.processingStatus = filters.processingStatus;

  return variables;
};

export const buildCanvassingSessionListVariables = ({
  filters,
  first,
  offset,
}: Omit<FetchCanvassingSessionsOptions, 'fetchImpl'>): CanvassingSessionListQueryVariables => ({
  first,
  offset,
  ...buildCanvassingSessionFilterVariables(filters),
});

export const fetchCanvassingSessions = async ({
  filters,
  first,
  offset,
  fetchImpl,
}: FetchCanvassingSessionsOptions): Promise<CanvassingSessionListQuery> => {
  const client = createAuthenticatedGraphQLClient({ env: resolveClientEnv(), fetchImpl });
  const variables = buildCanvassingSessionListVariables({ filters, first, offset });

  return client.request<CanvassingSessionListQuery>({
    document: CANVASSING_SESSION_LIST_DOCUMENT,
    variables,
  });
};

export const fetchCanvassingSessionFilterOptions = async ({
  fetchImpl,
}: FetchCanvassingSessionFilterOptionsOptions = {}): Promise<CanvassingSessionFilterOptionsQuery> => {
  const client = createAuthenticatedGraphQLClient({ env: resolveClientEnv(), fetchImpl });
  const variables: CanvassingSessionFilterOptionsQueryVariables = {};

  return client.request<CanvassingSessionFilterOptionsQuery>({
    document: CANVASSING_SESSION_FILTER_OPTIONS_DOCUMENT,
    variables,
  });
};

const normalizeTracePageSize = (first: number): number => {
  if (!Number.isSafeInteger(first) || first < 1) return CANVASSING_SESSION_GPS_TRACE_MAX_PAGE_SIZE;
  return Math.min(first, CANVASSING_SESSION_GPS_TRACE_MAX_PAGE_SIZE);
};

const normalizeTraceOffset = (offset: number): number =>
  Number.isSafeInteger(offset) && offset >= 0 ? offset : 0;

const normalizeTelemetryPageSize = (first: number): CanvassingSessionTelemetryPageSize =>
  first === 12 || first === 25 || first === 50 ? first : 25;

const normalizeTelemetryOffset = (offset: number): number =>
  Number.isSafeInteger(offset) && offset >= 0 ? offset : 0;

export const fetchCanvassingSessionGpsTrace = async ({
  sessionId,
  mode,
  first,
  offset,
  snapshot,
  signal,
  fetchImpl,
}: FetchCanvassingSessionGpsTraceOptions): Promise<CanvassingSessionGpsTraceQuery> => {
  const client = createAuthenticatedGraphQLClient({ env: resolveClientEnv(), fetchImpl });
  const variables: CanvassingSessionGpsTraceQueryVariables = {
    sessionId,
    mode,
    first: normalizeTracePageSize(first),
    offset: normalizeTraceOffset(offset),
    ...(snapshot === undefined ? {} : { snapshot }),
  };

  return client.request<CanvassingSessionGpsTraceQuery>({
    document: CANVASSING_SESSION_GPS_TRACE_DOCUMENT,
    variables,
    signal,
  });
};

export const interpretCanvassingSessionRecording = (
  result: CanvassingSessionRecordingResult,
): InterpretedCanvassingSessionRecording => {
  if (result == null) return { kind: 'unavailable' };
  if (result.status === CanvassingSessionRecordingStatus.NotMerged) return { kind: 'not-merged' };
  if (result.status === CanvassingSessionRecordingStatus.Unavailable) return { kind: 'unavailable' };
  if (result.status !== CanvassingSessionRecordingStatus.Ready) return { kind: 'unexpected' };

  const url = result.access?.url;
  if (typeof url !== 'string' || url.trim() === '') return { kind: 'unavailable' };

  return {
    kind: 'ready',
    url: url.trim(),
    warnings: result.warnings,
  };
};

export const fetchCanvassingSessionRecording = async ({
  sessionId,
  purpose,
  signal,
  fetchImpl,
}: FetchCanvassingSessionRecordingOptions): Promise<CanvassingSessionRecordingQuery> => {
  const client = createAuthenticatedGraphQLClient({ env: resolveClientEnv(), fetchImpl });
  const variables: CanvassingSessionRecordingQueryVariables = { sessionId, purpose };

  return client.request<CanvassingSessionRecordingQuery>({
    document: CANVASSING_SESSION_RECORDING_DOCUMENT,
    variables,
    signal,
  });
};

export const fetchCanvassingSessionTelemetry = async ({
  sessionId,
  first,
  offset,
  snapshot,
  category,
  failuresOnly,
  signal,
  fetchImpl,
}: FetchCanvassingSessionTelemetryOptions): Promise<CanvassingSessionTelemetryQuery> => {
  const client = createAuthenticatedGraphQLClient({ env: resolveClientEnv(), fetchImpl });
  const variables: CanvassingSessionTelemetryQueryVariables = {
    sessionId,
    first: normalizeTelemetryPageSize(first),
    offset: normalizeTelemetryOffset(offset),
    failuresOnly,
    ...(snapshot === undefined ? {} : { snapshot }),
    ...(category === undefined ? {} : { category }),
  };

  return client.request<CanvassingSessionTelemetryQuery>({
    document: CANVASSING_SESSION_TELEMETRY_DOCUMENT,
    variables,
    signal,
  });
};

const emptyPipelineJobs = (): CanvassingSessionDetailCycle['pipelineJobs'] => ({
  __typename: 'CanvassingSessionPipelineJobListResult',
  items: [],
  total: 0,
});

const emptyDoorVisits = (): CanvassingSessionDetailCycle['doorVisits'] => ({
  __typename: 'CanvassingSessionDoorVisitListResult',
  items: [],
  total: 0,
});

// Detail, pipeline jobs, and visit diagnostics are deliberately requested in one
// cycle. The server owns account scope, ordering, and all pagination boundaries.
export const fetchCanvassingSessionDetailCycle = async ({
  sessionId,
  pipelineJobs,
  doorVisits,
  signal,
  fetchImpl,
}: FetchCanvassingSessionDetailCycleOptions): Promise<CanvassingSessionDetailCycle> => {
  const client = createAuthenticatedGraphQLClient({ env: resolveClientEnv(), fetchImpl });
  const detailVariables: CanvassingSessionDetailQueryVariables = { sessionId };
  const pipelineJobVariables: CanvassingSessionPipelineJobListQueryVariables = {
    sessionId,
    first: pipelineJobs.first,
    offset: pipelineJobs.offset,
  };
  const doorVisitVariables: CanvassingSessionDoorVisitListQueryVariables = {
    sessionId,
    first: doorVisits.first,
    offset: doorVisits.offset,
  };

  const [detailResult, pipelineJobsResult, doorVisitsResult] = await Promise.allSettled([
    client.request<CanvassingSessionDetailQuery>({
      document: CANVASSING_SESSION_DETAIL_DOCUMENT,
      variables: detailVariables,
      signal,
    }),
    client.request<CanvassingSessionPipelineJobListQuery>({
      document: CANVASSING_SESSION_PIPELINE_JOB_LIST_DOCUMENT,
      variables: pipelineJobVariables,
      signal,
    }),
    client.request<CanvassingSessionDoorVisitListQuery>({
      document: CANVASSING_SESSION_DOOR_VISIT_LIST_DOCUMENT,
      variables: doorVisitVariables,
      signal,
    }),
  ]);

  if (detailResult.status === 'rejected') {
    throw detailResult.reason;
  }

  const detail = detailResult.value.canvassingSessionDetail;
  // A null detail is the authoritative unavailable response. The concurrently
  // requested nested diagnostics are intentionally discarded in that case.
  if (detail === null) {
    return { detail: null, pipelineJobs: emptyPipelineJobs(), doorVisits: emptyDoorVisits() };
  }

  if (pipelineJobsResult.status === 'rejected') {
    throw pipelineJobsResult.reason;
  }

  if (doorVisitsResult.status === 'rejected') {
    throw doorVisitsResult.reason;
  }

  return {
    detail,
    pipelineJobs: pipelineJobsResult.value.canvassingSessionPipelineJobList ?? emptyPipelineJobs(),
    doorVisits: doorVisitsResult.value.canvassingSessionDoorVisitList ?? emptyDoorVisits(),
  };
};
